import { SearchMapPriceLayoutContext } from 'components/Search/SearchResultsMap/SearchMapRedesignContext'
import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import CompactSearchTilePriceBlock from 'components/OfferList/OfferListTiles/Support/CompactSearchTilePriceBlock'
import BedbankMarginText from 'components/OfferPage/Bedbank/BedbankMargin'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { buildSuggestedDatesString } from 'lib/search/searchUtils'
import OfferListEventsContext, {
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import useOfferTilePriceBlockExperiment from 'hooks/Experiments/useOfferTilePriceBlockExperiment'
import useHotelSearchPriceAlignment from 'hooks/Experiments/useHotelSearchPriceAlignment'
import OfferTilePriceBlockCaption from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockCaption'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import styled from 'styled-components'

const PriceBlockExperimentStack = styled(Group)`
  text-align: end;
`

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  offerPrice: number
  offerMemberPrice: number
  isFlexibleSearch?: string
  suggestedDates?: {
    checkIn: string
    checkOut: string
  }
  nightsToDisplay: number
  promoteAsBundle: boolean
  saleUnit: string
  rooms: Array<App.Occupants>
  discountPercentage: number
  value: number
  hotelNights: number
  offerLocations: Array<string>
  displayMargin?: boolean
  margin?: number
  marginAud?: number
  supplier?: string
  directSearchPrices?: App.OfferListMetadataPricing
  shownRate?: {
    mobilePromotion?: boolean
  }
  bundlePropertyFees?: number
  propertyFees?: number
  bundleTaxesAndFees?: number
  taxesAndFees?: number
  bundlePrice: number
  hotelPrice: number
  flightPrice?: number
  bundleMemberPrice?: number
  hotelMemberPrice: number
  luxPlusLPPEnabled: boolean
  offerUrl: string
  hidePrice?: boolean
  hideIncludesTaxesAndFees?: boolean
  compactExperimentLayout?: boolean
  compactPriceSize?: 'S' | 'M' | 'L'
  pricingAlign?: 'start' | 'end'
  showPayAtPropertyPill?: boolean
  viewPriceInPoints?: boolean
}

function getPriceBeforePropertyFees(price: number | undefined, propertyFees: number | undefined) {
  if (!price || price <= 0) {
    return undefined
  }
  return Math.max(price - (propertyFees ?? 0), 0)
}

function BedbankSearchTileGarden({
  offer,
  offerPrice,
  offerMemberPrice,
  isFlexibleSearch,
  suggestedDates,
  nightsToDisplay,
  promoteAsBundle,
  saleUnit,
  rooms,
  discountPercentage,
  value,
  hotelNights,
  offerLocations,
  displayMargin,
  margin,
  marginAud,
  supplier,
  directSearchPrices,
  shownRate,
  bundlePropertyFees,
  propertyFees,
  bundleTaxesAndFees,
  taxesAndFees,
  bundlePrice,
  hotelPrice,
  flightPrice,
  bundleMemberPrice,
  hotelMemberPrice,
  luxPlusLPPEnabled,
  offerUrl,
  hidePrice = false,
  hideIncludesTaxesAndFees = false,
  compactExperimentLayout = false,
  compactPriceSize = 'L',
  pricingAlign: requestedPricingAlign = 'start',
  showPayAtPropertyPill = false,
  viewPriceInPoints = false,
}: Props) {
  const intl = useIntl()
  const mapLayout = useContext(SearchMapPriceLayoutContext)
  const {
    priceInPoints,
    memberPriceInPoints,
    pointsToBurn,
    hideZeroCash,
    shouldShowBurn,
  } = useSearchOfferBurnPricing({
    price: offerPrice,
    memberPrice: offerMemberPrice,
    loyaltyProductType: getLoyaltyProductType(offer) ?? 'hotel_lpp_eps',
    viewPriceInPoints,
  })
  const priceBlockVariation = useOfferTilePriceBlockExperiment(offer, !promoteAsBundle,
    !hidePrice && isDisplayablePrice(offerPrice) && !viewPriceInPoints && !shouldShowBurn)
  const alignWithPriceBlockExperiment = useHotelSearchPriceAlignment()
  const pricingAlign = alignWithPriceBlockExperiment ? 'end' : requestedPricingAlign
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  useEffect(() => {
    dispatchOfferListEvent({
      type: OfferListEvents.pricingCalculated,
      offerId: offer.id,
      availability: true,
      roomRateId: undefined,
      price: hidePrice ? undefined : offerPrice,
      memberPrice: hidePrice ? undefined : offerMemberPrice,
      duration: nightsToDisplay,
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const priceCaptionText = useMemo(() => {
    const datesOrNights = isFlexibleSearch && suggestedDates
      ? buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
      : intl.formatMessage(
          {
            id: 'm5L0sUHF1GtB591lBNfZ',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}',
          },
          { nights: nightsToDisplay },
        )
    if (promoteAsBundle) {
      return intl.formatMessage(
        {
          id: 'AKw8DCa-Jwzdz1UkVaeB',
          defaultMessage: '{datesOrNights} & flights from',
        },
        { datesOrNights },
      )
    }
    return intl.formatMessage(
      {
        id: 'FLVfJrwOOoEO_TBBIDUX',
        defaultMessage: '{datesOrNights} from',
      },
      { datesOrNights },
    )
  }, [isFlexibleSearch, suggestedDates, nightsToDisplay, promoteAsBundle, intl])

  const priceCaption = <OfferTilePriceBlockCaption
    priceBlockExperimentVariation={priceBlockVariation}
    duration={nightsToDisplay}
    checkIn={isFlexibleSearch ? suggestedDates?.checkIn : undefined}
    checkOut={isFlexibleSearch ? suggestedDates?.checkOut : undefined}
    saleUnit={saleUnit === 'person' ? saleUnit : offer.saleUnit}
    unitCount={saleUnit === 'person' ? 1 : rooms.length}
    isPricePerNight={saleUnit === 'night'}
  >{priceCaptionText}
  </OfferTilePriceBlockCaption>

  const showCompactDiscount = compactExperimentLayout &&
    discountPercentage >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const _propertyFees = promoteAsBundle ? bundlePropertyFees : propertyFees
  const _hotelPrice = promoteAsBundle ? bundlePrice : hotelPrice
  const _hotelMemberPrice = promoteAsBundle ?
    bundleMemberPrice :
    hotelMemberPrice
  const _hotelMemberPriceBeforePropertyFees = getPriceBeforePropertyFees(_hotelMemberPrice, _propertyFees)

  const bedbankPriceSaleUnit = (saleUnit === 'night' || saleUnit === 'person') ? saleUnit : 'total'

  let bedbankPriceDisplay: React.ReactNode
  if (shouldShowBurn) {
    bedbankPriceDisplay = <LoyaltyBurnPriceDisplay
      horizontalAlign={alignWithPriceBlockExperiment ? 'end' : undefined}
      totalPrice={offerPrice}
      pointsToBurn={pointsToBurn}
      priceInPoints={priceInPoints}
      memberPrice={offerMemberPrice}
      memberPriceInPoints={memberPriceInPoints}
      size={mapLayout ? 'M' : compactPriceSize}
      saleUnit={bedbankPriceSaleUnit}
      hideZeroCash={hideZeroCash}
    />
  } else {
    bedbankPriceDisplay = <LuxPlusPriceStack
      size={mapLayout ? 'M' : compactPriceSize}
      price={offerPrice}
      memberPrice={offerMemberPrice}
      saleUnit={saleUnit}
      horizontalAlign={pricingAlign}
      presentation={!mapLayout && priceBlockVariation ? 'price-block-experiment' : undefined}
    />
  }

  if (hidePrice) {
    return null
  }

  const showCompactPrice = !shouldShowBurn && (mapLayout === 'row' ||
    (!mapLayout && compactExperimentLayout && !priceBlockVariation))

  const pricingContent = (
    <>
      {!!offerPrice && <>
        {mapLayout && !showCompactPrice && <>{priceCaption}{bedbankPriceDisplay}</>}
        {!mapLayout && !compactExperimentLayout && (
          <Group direction="vertical" gap={8} horizontalAlign={pricingAlign} fullWidth>
            {showPayAtPropertyPill && (
              <PayAtPropertyPill wrap />
            )}
            {priceCaption}
          </Group>
        )}
        {showCompactPrice && (
          <CompactSearchTilePriceBlock
            mapRow={mapLayout === 'row'}
            durationText={priceCaptionText}
            price={offerPrice}
            memberPrice={offerMemberPrice}
            discountPercentage={discountPercentage}
            showDiscount={showCompactDiscount}
            saleUnit={saleUnit}
            size={compactPriceSize}
            offerConfig={offer.luxPlus}
          />
        )}
        {!mapLayout && (!compactExperimentLayout || shouldShowBurn || priceBlockVariation) && <>
          {compactExperimentLayout && !priceBlockVariation && showCompactDiscount && (
            <PriceRowValueDiscount discountPercentage={discountPercentage} />
          )}
          {compactExperimentLayout && !priceBlockVariation && (
            <Caption variant="medium" colour="neutral-two">
              {priceCaptionText}
            </Caption>
          )}
          {compactExperimentLayout && priceBlockVariation && priceCaption}
          {bedbankPriceDisplay}
        </>}
        {!compactExperimentLayout && offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
        {!mapLayout && !compactExperimentLayout && !priceBlockVariation && discountPercentage >= 5 && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={value}
          discountPercentage={discountPercentage}
          wrap="word-wrap"
        />}
      </>}

      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
      />

      {displayMargin && (
        <BedbankMarginText
          margin={margin!}
          marginAud={marginAud!}
          supplier={supplier!}
          type="bedbank_estimate"
        />
      )}
      {(directSearchPrices || (shownRate && hotelNights)) && (
        <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offer={offer}
          rooms={rooms}
          propertyFees={_propertyFees}
          taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
          hotelPrice={_hotelPrice - (_propertyFees ?? 0)}
          flightPrice={promoteAsBundle ? flightPrice : undefined}
          hotelMemberPrice={_hotelMemberPriceBeforePropertyFees}
          showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
          duration={hotelNights}
          cta={
            <TextButton kind="primary" fit="flex" to={offerUrl}>
              {intl.formatMessage({
                id: 'P2EB8s5UpskzMpdaUh_c',
                defaultMessage: 'View Offer',
              })}
            </TextButton>
          }
          context="search"
          hasMobilePromotion={shownRate?.mobilePromotion}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees || !!priceBlockVariation}
        />
      )}
    </>
  )

  // Keep the existing parent layout unless the search alignment experiment is active.
  return alignWithPriceBlockExperiment ? <PriceBlockExperimentStack direction="vertical" horizontalAlign="end" fullWidth>
    {pricingContent}
  </PriceBlockExperimentStack> : pricingContent
}

export default BedbankSearchTileGarden
